/**
 * Pinned pages, recently visited pages, and whose activity the feeds show.
 *
 * Kept in THIS browser (localStorage), per person and per organization: they
 * are a convenience for moving around, not business data, so they never touch
 * the API or the data model. Losing them — a new browser, cleared storage —
 * costs nothing but a click.
 *
 * `activityScope` is 'mine' or 'all': an administrator's choice between their
 * own activity and everyone's in the dashboard and notification feeds. It
 * starts at 'mine' — a feed of what you did — and only matters to someone who
 * holds audit.read; everyone else is shown their own regardless.
 *
 * Each entry is { to, label, app, at }: `to` is organization-relative (as the
 * app's links are), so a pinned page follows the organization it was pinned
 * in and never leaks into another one.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';

const RECENT_LIMIT = 8;
const PINNED_LIMIT = 12;
const EMPTY = Object.freeze({ pinned: [], recent: [], activityScope: 'mine' });

const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const keyFor = (scope) => `nexerp.workspace.${scope}`;

// A tiny read cache, so useSyncExternalStore sees the SAME object until the
// data really changes (it compares snapshots by identity).
const cache = new Map();

function read(scope) {
  if (!scope) return EMPTY;
  const raw = (() => {
    try {
      return localStorage.getItem(keyFor(scope));
    } catch {
      return null;
    }
  })();
  const hit = cache.get(scope);
  if (hit && hit.raw === raw) return hit.value;

  let value = EMPTY;
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    value = {
      pinned: Array.isArray(parsed?.pinned) ? parsed.pinned : [],
      recent: Array.isArray(parsed?.recent) ? parsed.recent : [],
      activityScope: parsed?.activityScope === 'all' ? 'all' : 'mine',
    };
  } catch {
    value = EMPTY;
  }
  cache.set(scope, { raw, value });
  return value;
}

function write(scope, value) {
  if (!scope) return;
  try {
    localStorage.setItem(keyFor(scope), JSON.stringify(value));
  } catch {
    // Storage full or disabled: the preference simply is not kept.
  }
  notify();
}

/** The path a page is remembered by: the path plus any query, never a hash. */
const clean = (entry) => ({
  to: String(entry.to),
  label: String(entry.label ?? '').slice(0, 120),
  app: entry.app ?? null,
  at: entry.at ?? Date.now(),
});

export function recordVisit(scope, entry) {
  if (!scope || !entry?.to || !entry?.label) return;
  const current = read(scope);
  const next = clean(entry);
  const existing = current.recent[0];
  // Re-rendering the same page must not rewrite storage on every render.
  if (existing && existing.to === next.to && existing.label === next.label) return;
  const recent = [next, ...current.recent.filter((item) => item.to !== next.to)].slice(0, RECENT_LIMIT);
  write(scope, { ...current, recent });
}

export function togglePinned(scope, entry) {
  if (!scope || !entry?.to) return;
  const current = read(scope);
  const isPinned = current.pinned.some((item) => item.to === entry.to);
  const pinned = isPinned
    ? current.pinned.filter((item) => item.to !== entry.to)
    : [clean(entry), ...current.pinned].slice(0, PINNED_LIMIT);
  write(scope, { ...current, pinned });
}

export function clearRecent(scope) {
  if (!scope) return;
  write(scope, { ...read(scope), recent: [] });
}

export function setActivityScope(scope, value) {
  if (!scope) return;
  write(scope, { ...read(scope), activityScope: value === 'all' ? 'all' : 'mine' });
}

/** Reads and changes this person's workspace preferences in this organization. */
export function useWorkspacePrefs(scope) {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => read(scope),
    () => EMPTY
  );

  const isPinned = useCallback((to) => snapshot.pinned.some((item) => item.to === to), [snapshot]);

  return useMemo(
    () => ({
      pinned: snapshot.pinned,
      recent: snapshot.recent,
      isPinned,
      togglePinned: (entry) => togglePinned(scope, entry),
      recordVisit: (entry) => recordVisit(scope, entry),
      clearRecent: () => clearRecent(scope),
      activityScope: snapshot.activityScope,
      setActivityScope: (value) => setActivityScope(scope, value),
    }),
    [snapshot, isPinned, scope]
  );
}

// --- the context navigation's collapsed state ----------------------------------

const NAV_KEY = 'nexerp.nav';

export function getNavCollapsed() {
  try {
    return localStorage.getItem(NAV_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

export function setNavCollapsed(collapsed) {
  try {
    if (collapsed) localStorage.setItem(NAV_KEY, 'collapsed');
    else localStorage.removeItem(NAV_KEY);
  } catch {
    /* ignored */
  }
  notify();
}

export function useNavCollapsed() {
  const collapsed = useSyncExternalStore(subscribe, getNavCollapsed, () => false);
  return [collapsed, setNavCollapsed];
}
